/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import * as React from 'react';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods,
    IGroup,
    IGroupDelivery,
    IGroups,
    IOrderInformation,
    ISalesLine,
    IOrderConfirmationViewProps,
    IOrderConfirmationData
} from '@msdyn365-commerce-modules/order-management';
import { IProductListResources, ProductListComponent, ProductListProduct } from '../../../components/product-list-component/product-list-component';
import { IOrderConfirmationResources } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import { AsyncResult, Customer, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { getCustomerAgreements } from '../../../shared-utils/agreement-product-utils';
import { getAllCharges } from '../../../shared-utils/cart-utils';
import { IProductAgreement } from '../../../shared-utils/types';

interface IIncludeViewProps {
  viewProps: IOrderConfirmationViewProps;
  customerAgreements?: IProductAgreement[];
};

function salesLines2ProductListProduct(salesLines: ISalesLine[]) {
    return salesLines.map(x => (
        {
            CartLine: x.data.salesLine,
            CurrentQuantity: x.data.salesLine.Quantity,
            Product: x.data.product,
            DefaultUnitOfMeasure: x.data.product?.DefaultUnitOfMeasure,
            ItemId: x.data.salesLine.ItemId,
            Name: x.data.product?.Name,
            Price: x.data.salesLine.NetAmount,
            RecordId: x.data.product?.RecordId,
            PrimaryImageUrl: x.data.product?.PrimaryImageUrl,
            RequestedDeliveryDate: x.data.salesLine.RequestedDeliveryDate
        } as ProductListProduct
    )).sort((a, b) => {
        if (!a.RequestedDeliveryDate) return 1;
        if (!b.RequestedDeliveryDate) return -1;
        return new Date(b.RequestedDeliveryDate).getTime() - new Date(a.RequestedDeliveryDate).getTime();
    });
}

interface IOrderInformationExt extends IOrderInformation {
    context: ICoreContext;
    baseClassName?: string;
    resources: IOrderConfirmationResources;
    salesOrder?: SalesOrder;
}

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformationExt> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId,
    context,
    baseClassName,
    resources,
    salesOrder
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {channelReferenceId}
        {receiptEmail}
        {salesOrder?.RequestedDeliveryDate && (
            <span className={`${baseClassName}__order-information-requested-delivery-date`}>
                {resources.requestedDeliveryDateText}: <span>{MsDyn365.isBrowser ? context.cultureFormatter.formatDate(salesOrder.RequestedDeliveryDate) : ''}</span>
            </span>
        )}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo,
    pickupDateTimeslot
}) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IIncludeViewProps & IGroup> = ({
    viewProps,
    groupProps,
    delivery,
    salesLinesProps,
    salesLines,
    customerAgreements
}) => {
    const resources = viewProps.resources as IOrderConfirmationResources;
    const productListResources: IProductListResources = {
        deliveryDateLabel: resources.salesLineDeliveryDateLabel,

        // There are not required for this view. Leave as number incase we need to add them later.
        buyNowButtonText: '1',
    };

    return (
        <Node {...groupProps}>
            {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
            {/* {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map((salesLine: ISalesLine) => (
                        <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                    ))}
                </Node>
            )} */}
            <div>
                <ProductListComponent
                    context={viewProps.context}
                    moduleId={viewProps.id}
                    moduleType={viewProps.typeName}
                    imageSettings={viewProps.config.imageSettings}
                    products={salesLines2ProductListProduct(salesLines)}
                    simpleProducts={viewProps.data.orderHydration.result?.products || []}
                    resources={productListResources}
                    readOnly
                    showGroups
                    smallestImage
                    historicalPrice
                    customerAgreements={customerAgreements} />
            </div>
        </Node>
    );
};

export const OrderConfirmationGroups: React.FC<IIncludeViewProps & IGroups> = ({ viewProps, groupsProps, groups, customerAgreements }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup key={index} viewProps={viewProps} customerAgreements={customerAgreements} {...group} />
        ))}
    </Node>
);

interface IOrderSummaryExtended extends IOrderSummary {
    containerDepositText: string;
    customerAgreements?: IProductAgreement[];
    salesOrder?: SalesOrder;
}

export const OrderConfirmationOrderSummary: React.FC<IOrderSummaryExtended> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    containerDepositText,
    salesOrder
}) => {
    const charges = getAllCharges(salesOrder);
    const orderSummaryLines = [];

    if (charges) {
        for (const charge of charges) {
            orderSummaryLines.push(React.cloneElement(subtotal as JSX.Element, {
                label: charge.Description,
                name: charge.ChargeCode.toLowerCase().replace(/ /g, '-'),
                value: charge.TotalCalculatedAmount
            }));
        }
    }

    // Shipping field shouldn't be used, but in case it is, hide it when its value is 0/undefined
    const fixedShipping = ((shipping as JSX.Element)?.props.value || 0) > 0
        ? shipping
        : null;

    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {orderSummaryLines}
            {fixedShipping}
            {tax}
            {/* <p className='ms-order-confirmation__order-summary-line-container-deposit'>
                <span>* {containerDepositText}</span>
            </p> */}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

export interface IOrderConfirmationDataExt extends IOrderConfirmationData {
    orgInformation: AsyncResult<Customer>;
}

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = (props) => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        context
    } = props;
    const { orgInformation } = props.data as IOrderConfirmationDataExt;

    const resources = props.resources as IOrderConfirmationResources;

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation
                {...orderInfomation}
                context={props.context}
                baseClassName={props.moduleProps.className}
                resources={props.resources as IOrderConfirmationResources}
                salesOrder={props.data.orderHydration.result?.salesOrder}
            />}
            {groups && (
                <OrderConfirmationGroups viewProps={props} {...groups} customerAgreements={getCustomerAgreements(context, orgInformation.result?.Attributes)} />
            )}
            {payment && <OrderConfirmationPayment {...payment} />}
            {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} containerDepositText={resources.containerDepositText} salesOrder={props.data.orderHydration.result?.salesOrder} />}
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
